// Import third party modules
import createKindeClient from "@kinde-oss/kinde-auth-pkce-js";

// Import project modules
import Request from "../common/Request.js";

/**
 * @description A FamStat user class that combines aspects of the Kinde auth system with our own FamStat user data
 * @export
 * @class User
 */
export default class User {
  #data = {
    // TODO: Figure out minimal set of properties that must present a default value
    userId: "",
    emailAddress: "",
    firstName: "",
    lastName: "",
    avatar: "",
    location: {},
  };
  #kindeClient;
  #kindeProfile;
  #config;
  #invitation;
  #token = "";
  #authenticated = false;

  /**
   * Creates an instance of User.
   * @param {object} [config={}] The configuration object compiled from config.js via build.sh
   * @param {object} [params={}] Any query parameters from the URL
   * @memberof User
   */
  constructor(config = {}, params = {}) {
    // Cache the provided options
    this.#config = config;

    if (params.invitation) {
      this.#invitation = params.invitation;
    }
  }

  /**
   * @description Initialises the user from their Kinde profile and their FamStat data
   * @memberof User
   */
  async init() {
    // Create a new Kinde client with authentication details from config.js and callback handlers for login and invitation
    this.#kindeClient = await createKindeClient({
      ...this.#config.auth,

      on_redirect_callback: async (user, appState) => {
        if (appState.action === "login") {
          this.#kindeProfile = user;
          this.#token = user.token;
        }

        if (appState.invitation) {
          // Store the invitation, returned from a register or login, for later use
          this.#invitation = appState.invitation;
        }
      },
    });

    // Check if we have a Kinde profile (we do get one from the login callback)
    if (!this.#kindeProfile) {
      // Try the local cache first, then go across the network to Kinde.
      this.#kindeProfile = this.#kindeClient.getUser();
      if (!this.#kindeProfile && (await this.#kindeClient.isAuthenticated())) {
        this.#kindeProfile = await this.#kindeClient.getUserProfile();
      }
    }

    // If we have a Kinde profile, then we are authenticated into Kinde, now we need the Kinde token and FamStat data
    if (this.#kindeProfile != null) {
      // Set some of the #data properties we know so far
      this.#data.emailAddress = this.#kindeProfile.email;
      this.#data.avatar = this.#kindeProfile.picture;

      // Get and save the token which we will need for our own API calls to FamStat
      this.#token = await this.#kindeClient.getToken();

      // We have a valid Kinde profile so let's fetch the extended data from FamStat
      const data = await this.#readUser(this.#data.emailAddress);
      if (data) {
        this.#data = data;
        // The user is both Kinde and FamStat authenticated
        this.#authenticated = true;
      }
    }

    // Check if the user has an invitation pending and handle it
    if (this.#invitation) {
      if (this.#authenticated) {
        // Handle immediately if already authenticated
        this.#handleInvitation(this.#invitation);
      } else {
        // Call login with the invitation to handle it after login
        // Note: we don't know if the user needs to login or register at this time
        this.login({ invitation: this.#invitation });
      }
    }
  }

  /**
   * @description The method to handle the user accepting an invitation.
   * @param {string} invitation: The invitation token
   * @returns {Promise<void>}
   * @memberof UserService
   * @public
   */
  async #handleInvitation(invitation) {
    const familyId = this.#parseTokenPayload(invitation).familyId;
    await Request.post(`/api/families/${familyId}/members`, { invitation }, { token: this.#token });

    // TODO: Be more graceful, remove just the invitation params from the URL. Other params are other responsibilities.
    window.location.href = "/status";
  }

  /**
   * @description The method to handle parsing the token payload as JSON.
   * @param {string} token:  The token to parse
   * @returns {Object}:      The parsed token
   * @note Parses only, does not validate.
   * @private
   * @memberof UserService
   */
  #parseTokenPayload(token) {
    // TODO: Handle any errors gracefully

    const payload = token.split(".")[1];
    const decoded = window.atob(payload);
    return JSON.parse(decoded);
  }

  /**
   * @description The user's authentication status as a combination of Kinde and FamStat
   * @readonly
   * @memberof User
   */
  get isAuthenticated() {
    return this.#authenticated;
  }

  // FKA this.getProfile()

  /**
   * @description The user's data from the FamStat API (with some Kinde data)
   * @readonly
   * @note This was formerly called getProfile() but it was getting confused between Kinde and FamStat
   * @memberof User
   */
  get data() {
    return this.#data;
  }

  /**
   * @description The user's Kinde token for use with the Kinde AND FamStat APIs
   * @readonly
   * @memberof User
   */
  get token() {
    return this.#token;
  }

  /**
   * @description The method to handle registering the user via Kinde. Kinde will update our API via a webhook.
   * @return {*}
   * @memberof User
   */
  async register(additionalAppState = {}) {
    return this.#kindeClient.register({ app_state: { action: "register", ...additionalAppState } });
  }

  /**
   * The method to handle logging the user in via the auth provider.
   */
  async login(additionalAppState = {}) {
    return this.#kindeClient.login({ app_state: { action: "login", ...additionalAppState } });
  }

  /**
   * The method to handle logging the user out via the auth provider.
   */
  async logout() {
    return this.#kindeClient.logout({ app_state: { action: "logout" } });
  }

  /**
   * Creates a status via the API.
   */
  async updateUserStatus({ status, location }) {
    // Create an object from the status and location
    const payload = { status, location };

    // Attempt to update the status via the API
    const response = await Request.put(`/api/users/${this.#data.emailAddress}/status`, payload, {
      token: this.#token,
    });
  }

  /**
   * Reads the families via the API.
   */
  async readFamilies() {
    const response = await Request.get("/api/families", {
      token: this.#token,
    });

    if (response?.ok) {
      return response.json();
    }
  }

  /**
   * Creates a new family via the API.
   */
  async createFamily({ name }) {
    // Create a new family object from the name
    const user = { name };

    // Attempt to create the family in the system
    return await Request.post("/api/families", user, {
      token: this.#token,
    });
  }

  /**
   * Reads a family via the API.
   */
  async readFamilyByFamilyId(familyId) {
    const response = await Request.get(`/api/families/${familyId}`, {
      token: this.#token,
    });

    if (response?.ok) {
      return response.json();
    }
  }

  /**
   * Creates a new family member via the API.
   */
  async createFamilyMember(familyId, user) {
    // Attempt to create the family member in the system
    return await Request.post(`/api/families/${familyId}/invitations`, user, { token: this.#token });
  }

  /**
   * Sends a message via the API.
   */
  async sendMessage(userId, text, subject = "Notification") {
    return await Request.post(
      "/api/messages",
      {
        text,
        subject,
        transport: {
          push: {
            replyTo: "replySubId",
            to: [userId],
            action: "CUT",
          },
        },
      },
      {
        token: this.#token,
      }
    );
  }

  // PRIVATE METHODS

  async #readUser(username) {
    const response = await Request.get(`/api/users/${username}`, {
      token: this.#token,
    });

    if (response?.ok) {
      return response.json();
    }

    if (response.status === 403 && response.statusText === "User is suspended") {
      window.alert("This account has been suspended. Please contact support.");
      this.kinde.logout();
      // TODO build this into globalish function or middleware so that 401s and 403s can be properly surfaced, ui logged out, etc.
    } else {
      console.log("Error", response.status, response.statusText);
      throw new Error(response.status);
    }
  }
}
